if (!customElements.get('hydros-product-switcher')) {
  customElements.define(
    'hydros-product-switcher',
    class HydrosProductSwitcher extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('[data-home-slide]')];
        this.tabs = [...this.querySelectorAll('[data-home-slide-button]')];
        this.pauseButton = this.querySelector('[data-home-pause]');
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
        this.userPaused = false;

        this.handleTabClick = (event) => this.show(event.currentTarget.dataset.homeSlideButton);
        this.handlePauseClick = () => this.togglePause();
        this.handleMouseEnter = () => this.stopTimer();
        this.handleMouseLeave = () => this.schedule();
        this.handleFocusIn = () => this.stopTimer();
        this.handleFocusOut = (event) => {
          if (!this.contains(event.relatedTarget)) this.schedule();
        };
        this.handleVisibility = () => {
          if (document.hidden) this.stopTimer();
          else this.schedule();
        };

        this.tabs.forEach((tab) => tab.addEventListener('click', this.handleTabClick));
        this.pauseButton?.addEventListener('click', this.handlePauseClick);
        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
        document.addEventListener('visibilitychange', this.handleVisibility);

        this.show(this.slides[this.index]?.dataset.homeSlide || 'hynox', false);
        this.schedule();
      }

      show(name, restart = true) {
        const nextIndex = this.slides.findIndex((slide) => slide.dataset.homeSlide === name);
        if (nextIndex < 0) return;

        this.index = nextIndex;
        this.dataset.activeProduct = name;

        this.slides.forEach((slide, index) => {
          const active = index === nextIndex;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        this.tabs.forEach((tab) => {
          const active = tab.dataset.homeSlideButton === name;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
        });

        if (restart) this.schedule();
      }

      advance() {
        const next = (this.index + 1) % this.slides.length;
        this.show(this.slides[next].dataset.homeSlide);
      }

      togglePause() {
        this.userPaused = !this.userPaused;
        this.classList.toggle('is-paused', this.userPaused);
        this.pauseButton?.setAttribute('aria-label', this.userPaused ? 'Reprendre le défilement' : 'Mettre le défilement en pause');
        this.pauseButton?.setAttribute('title', this.userPaused ? 'Lecture' : 'Pause');
        if (this.userPaused) this.stopTimer();
        else this.schedule();
      }

      stopTimer() {
        window.clearTimeout(this.timer);
      }

      schedule() {
        this.stopTimer();
        if (this.userPaused || this.motionQuery.matches || this.slides.length < 2 || document.hidden) return;
        this.timer = window.setTimeout(() => this.advance(), 7000);
      }

      disconnectedCallback() {
        this.stopTimer();
        this.tabs?.forEach((tab) => tab.removeEventListener('click', this.handleTabClick));
        this.pauseButton?.removeEventListener('click', this.handlePauseClick);
        this.removeEventListener('mouseenter', this.handleMouseEnter);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.removeEventListener('focusout', this.handleFocusOut);
        document.removeEventListener('visibilitychange', this.handleVisibility);
      }
    }
  );
}

if (!customElements.get('hydros-floating-hero')) {
  customElements.define(
    'hydros-floating-hero',
    class HydrosFloatingHero extends HTMLElement {
      connectedCallback() {
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.pointerQuery = window.matchMedia('(pointer: fine)');
        this.reelClips = [...this.querySelectorAll('[data-hero-reel-clip]')];
        this.updateReelPosters();
        this.reelToggle = this.querySelector('[data-hero-reel-toggle]');
        this.reelIndex = 0;
        this.reelUserPaused = false;
        this.reelIsVisible = true;
        this.update = this.update.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerLeave = this.handlePointerLeave.bind(this);
        this.handleReelToggle = this.handleReelToggle.bind(this);
        this.handleVisibility = this.handleVisibility.bind(this);
        this.handleMotionChange = this.handleMotionChange.bind(this);
        this.reelToggle?.addEventListener('click', this.handleReelToggle);
        this.motionQuery.addEventListener('change', this.handleMotionChange);
        window.addEventListener('scroll', this.update, { passive: true });
        window.addEventListener('resize', this.update, { passive: true });
        document.addEventListener('visibilitychange', this.handleVisibility);
        if (this.pointerQuery.matches) {
          this.addEventListener('pointermove', this.handlePointerMove, { passive: true });
          this.addEventListener('pointerleave', this.handlePointerLeave);
        }
        this.handleMotionChange();
      }

      initReel() {
        this.reelObserver = new IntersectionObserver((entries) => {
          this.reelIsVisible = entries[0]?.isIntersecting ?? true;
          if (!this.reelIsVisible) {
            this.stopReel();
            this.reelClips.forEach((clip) => clip.pause());
          } else if (!this.reelUserPaused && !this.motionQuery.matches && !document.hidden) {
            this.reelClips[this.reelIndex]?.play().catch(() => {});
            this.scheduleReel();
          }
        }, { threshold: .18 });
        this.reelObserver.observe(this);
        this.showReelClip(0);
      }

      loadReelClip(clip) {
        if (!clip || clip.dataset.heroLoaded) return;
        clip.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        clip.dataset.heroLoaded = 'true';
        clip.load();
      }

      updateReelPosters() {
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        this.reelClips?.forEach((clip) => {
          const poster = mobile ? clip.dataset.posterMobile : clip.dataset.posterDesktop;
          if (poster && clip.getAttribute('poster') !== poster) clip.setAttribute('poster', poster);
        });
      }

      showReelClip(index) {
        if (this.motionQuery.matches || document.hidden || !this.isConnected) return;
        const next = this.reelClips[index];
        if (!next) return;
        const previous = this.reelClips[this.reelIndex];
        this.loadReelClip(next);

        const activate = () => {
          if (!this.isConnected || this.motionQuery.matches) return;
          this.reelIndex = index;
          this.reelClips.forEach((clip) => clip.classList.toggle('is-active', clip === next));
          next.currentTime = 0;
          if (!this.reelUserPaused && this.reelIsVisible && !document.hidden) next.play().catch(() => {});
          window.clearTimeout(this.reelPauseTimer);
          this.reelPauseTimer = window.setTimeout(() => {
            if (previous && previous !== next) previous.pause();
          }, 950);
          this.scheduleReel();
        };

        if (next.readyState >= 2) activate();
        else next.addEventListener('loadeddata', activate, { once: true });
      }

      scheduleReel() {
        this.stopReel();
        if (this.motionQuery.matches || this.reelUserPaused || !this.reelIsVisible || document.hidden || this.reelClips.length < 2) return;
        this.reelTimer = window.setTimeout(() => {
          this.showReelClip((this.reelIndex + 1) % this.reelClips.length);
        }, 5600);
      }

      stopReel() {
        window.clearTimeout(this.reelTimer);
      }

      handleReelToggle() {
        if (this.motionQuery.matches || navigator.connection?.saveData) return;
        this.reelUserPaused = !this.reelUserPaused;
        this.classList.toggle('is-reel-paused', this.reelUserPaused);
        this.reelToggle?.setAttribute('aria-pressed', this.reelUserPaused ? 'true' : 'false');
        this.reelToggle?.setAttribute('aria-label', this.reelUserPaused ? 'Reprendre l’animation du hero' : 'Mettre l’animation du hero en pause');
        this.reelToggle?.setAttribute('title', this.reelUserPaused ? 'Lecture' : 'Pause');
        if (this.reelUserPaused) {
          this.stopReel();
          this.reelClips.forEach((clip) => clip.pause());
        } else if (this.reelIsVisible && !document.hidden) {
          this.reelClips[this.reelIndex]?.play().catch(() => {});
          this.scheduleReel();
        }
      }

      handleVisibility() {
        if (document.hidden || this.motionQuery.matches || navigator.connection?.saveData || !this.reelObserver) {
          this.stopReel();
          this.reelClips.forEach((clip) => clip.pause());
        } else if (!this.reelUserPaused && this.reelIsVisible) {
          this.reelClips[this.reelIndex]?.play().catch(() => {});
          this.scheduleReel();
        }
      }

      handleMotionChange() {
        const usePoster = this.motionQuery.matches || navigator.connection?.saveData || typeof IntersectionObserver !== 'function';
        this.reelToggle?.toggleAttribute('hidden', usePoster || !this.reelClips.length);
        if (usePoster) {
          this.stopReel();
          this.reelClips.forEach((clip) => clip.pause());
          if (this.frame) cancelAnimationFrame(this.frame);
          if (this.pointerFrame) cancelAnimationFrame(this.pointerFrame);
          this.frame = null;
          this.pointerFrame = null;
          [...this.style].filter((property) => property.startsWith('--hero-')).forEach((property) => this.style.removeProperty(property));
          return;
        }
        if (this.reelClips.length && !this.reelObserver) this.initReel();
        else this.handleVisibility();
        this.update();
      }

      handlePointerMove(event) {
        if (this.motionQuery.matches) return;
        const rect = this.getBoundingClientRect();
        const x = Math.min(1, Math.max(-1, ((event.clientX - rect.left) / Math.max(1, rect.width) - 0.5) * 2));
        const y = Math.min(1, Math.max(-1, ((event.clientY - rect.top) / Math.max(1, rect.height) - 0.5) * 2));
        this.pointerState = { x, y };
        if (this.pointerFrame) return;
        this.pointerFrame = requestAnimationFrame(() => {
          const state = this.pointerState || { x: 0, y: 0 };
          this.style.setProperty('--hero-hynox-pointer-x', `${(-18 * state.x).toFixed(2)}px`);
          this.style.setProperty('--hero-hynox-pointer-y', `${(-12 * state.y).toFixed(2)}px`);
          this.style.setProperty('--hero-hynox-tilt-x', `${(-2.8 * state.y).toFixed(2)}deg`);
          this.style.setProperty('--hero-hynox-tilt-y', `${(-5 + 5 * state.x).toFixed(2)}deg`);
          this.style.setProperty('--hero-lumos-pointer-x', `${(9 * state.x).toFixed(2)}px`);
          this.style.setProperty('--hero-lumos-pointer-y', `${(-6 * state.y).toFixed(2)}px`);
          this.style.setProperty('--hero-lumos-tilt-x', `${(-1.4 * state.y).toFixed(2)}deg`);
          this.style.setProperty('--hero-lumos-tilt-y', `${(3 + 2.5 * state.x).toFixed(2)}deg`);
          this.pointerFrame = null;
        });
      }

      handlePointerLeave() {
        this.pointerState = { x: 0, y: 0 };
        this.style.setProperty('--hero-hynox-pointer-x', '0px');
        this.style.setProperty('--hero-hynox-pointer-y', '0px');
        this.style.setProperty('--hero-hynox-tilt-x', '0deg');
        this.style.setProperty('--hero-hynox-tilt-y', '-5deg');
        this.style.setProperty('--hero-lumos-pointer-x', '0px');
        this.style.setProperty('--hero-lumos-pointer-y', '0px');
        this.style.setProperty('--hero-lumos-tilt-x', '0deg');
        this.style.setProperty('--hero-lumos-tilt-y', '3deg');
      }

      update() {
        this.updateReelPosters();
        if (this.motionQuery.matches || this.frame) return;
        this.frame = requestAnimationFrame(() => {
          const rect = this.getBoundingClientRect();
          const progress = Math.min(1, Math.max(0, -rect.top / Math.max(1, rect.height)));
          this.style.setProperty('--hero-copy-y', `${(-24 * progress).toFixed(2)}px`);
          this.style.setProperty('--hero-copy-opacity', (1 - 0.42 * progress).toFixed(3));
          this.style.setProperty('--hero-hynox-x', `${(-22 * progress).toFixed(2)}px`);
          this.style.setProperty('--hero-hynox-y', `${(-76 * progress).toFixed(2)}px`);
          this.style.setProperty('--hero-hynox-rotate', `${(-5 - 3 * progress).toFixed(2)}deg`);
          this.style.setProperty('--hero-lumos-x', `${(26 * progress).toFixed(2)}px`);
          this.style.setProperty('--hero-lumos-y', `${(-112 * progress).toFixed(2)}px`);
          const lumosBaseRotate = window.matchMedia('(max-width: 749px)').matches ? 13 : 9;
          this.style.setProperty('--hero-lumos-rotate', `${(lumosBaseRotate + 3 * progress).toFixed(2)}deg`);
          this.frame = null;
        });
      }

      disconnectedCallback() {
        this.stopReel();
        window.clearTimeout(this.reelPauseTimer);
        this.reelObserver?.disconnect();
        this.reelClips?.forEach((clip) => clip.pause());
        this.reelToggle?.removeEventListener('click', this.handleReelToggle);
        this.motionQuery?.removeEventListener('change', this.handleMotionChange);
        document.removeEventListener('visibilitychange', this.handleVisibility);
        window.removeEventListener('scroll', this.update);
        window.removeEventListener('resize', this.update);
        this.removeEventListener('pointermove', this.handlePointerMove);
        this.removeEventListener('pointerleave', this.handlePointerLeave);
        if (this.frame) cancelAnimationFrame(this.frame);
        if (this.pointerFrame) cancelAnimationFrame(this.pointerFrame);
      }
    }
  );
}
